import eq from "lodash-es/eq"
import find from "lodash-es/find"
import get from "lodash-es/get"
import last from "lodash-es/last"
import { shallowEqual, useSelector } from "react-redux"

import type { IRootState } from "@/3514/store"

import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatMessageAuthor as EMessageAuthor,
    type TCoachingChatConversationError as TError,
    type TCoachingChatConversationMessage as TMessage,
    type TCoachingChatSummaryCard as TSummaryCard
} from "../types"

import {
    COACHING_CHATS_REDUCER_NAME,
    type TCoachingChatConversationState as TConversationState,
    type TCoachingChatFormState as TFormState,
    type TCoachingChatState as TState
} from "./coaching-chat.slice"

function _conversationGetter(state: IRootState, entityId: number): TConversationState {
    return get<TState>(state[COACHING_CHATS_REDUCER_NAME].chats, entityId)?.conversation
}

function useCoachingChatFormSelector(entityId: number): TFormState {
    return useSelector(
        (state: IRootState): TFormState => get<TState>(state[COACHING_CHATS_REDUCER_NAME].chats, entityId)?.form,
        shallowEqual
    )
}

function useCoachingChatConversationSelector(entityId: number): TConversationState {
    return useSelector((state: IRootState): TConversationState => _conversationGetter(state, entityId), shallowEqual)
}

function useCoachingChatConversationMessageByIdSelector(
    entityId: number,
    clientMessageId: string
): TMessage | undefined {
    return useSelector((state: IRootState): TMessage | undefined => {
        const _conversation: TConversationState | undefined = _conversationGetter(state, entityId)

        return find<TMessage>(_conversation?.messages, { clientMessageId })
    }, shallowEqual)
}

function useCoachingChatConversationLastMessageSelector(entityId: number): TMessage | null {
    return useSelector(
        (state: IRootState): TMessage | null => last<TMessage>(_conversationGetter(state, entityId)?.messages),
        shallowEqual
    )
}

function useCoachingChatConversationLastMessageAuthorIsUserSelector(entityId: number): boolean {
    return useSelector(
        (state: IRootState): boolean =>
            eq(last<TMessage>(_conversationGetter(state, entityId)?.messages)?.author, EMessageAuthor.User),
        shallowEqual
    )
}

function useCoachingChatConversationErrorSelector(entityId: number): TError {
    return useSelector((state: IRootState): TError => _conversationGetter(state, entityId)?.error, shallowEqual)
}

function useCoachingChatConversationStatusSelector(entityId: number): EConversationStatus {
    return useSelector(
        (state: IRootState): EConversationStatus => _conversationGetter(state, entityId)?.status,
        shallowEqual
    )
}

function useCoachingChatConversationIdSelector(entityId: number): number | null {
    return useSelector((state: IRootState): number | null => _conversationGetter(state, entityId)?.id, shallowEqual)
}

function useCoachingChatConversationSummarySelector(entityId: number): TSummaryCard[] {
    return useSelector(
        (state: IRootState): TSummaryCard[] => _conversationGetter(state, entityId)?.summary,
        shallowEqual
    )
}

function useCoachingChatInputValueSelector(entityId: number): string {
    return useSelector((state: IRootState): string => _conversationGetter(state, entityId)?.inputValueRaw, shallowEqual)
}

function useCoachingChatInputStateSelector(entityId: number): { isDimmed: boolean; isDisabled: boolean } {
    return useSelector((state: IRootState): { isDimmed: boolean; isDisabled: boolean } => {
        const conversation: TConversationState = _conversationGetter(state, entityId)

        return { isDimmed: conversation?.isInputDimmed, isDisabled: conversation?.isInputDisabled }
    }, shallowEqual)
}

function useCoachingChatMessagesIdsSelector(entityId: number): string[] {
    return useSelector(
        (state: IRootState): string[] => get<TState>(state[COACHING_CHATS_REDUCER_NAME].chats, entityId).messagesIds,
        shallowEqual
    )
}

function useCoachingChatConversationMessagesPresenceSelector(entityId: number): boolean {
    return useSelector(
        (state: IRootState): boolean =>
            get<TState>(state[COACHING_CHATS_REDUCER_NAME].chats, entityId).messagesIds.length > 0,
        shallowEqual
    )
}

export {
    useCoachingChatFormSelector,
    useCoachingChatConversationSelector,
    useCoachingChatConversationMessageByIdSelector,
    useCoachingChatConversationMessagesPresenceSelector,
    useCoachingChatConversationLastMessageSelector,
    useCoachingChatConversationLastMessageAuthorIsUserSelector,
    useCoachingChatConversationErrorSelector,
    useCoachingChatConversationStatusSelector,
    useCoachingChatConversationIdSelector,
    useCoachingChatConversationSummarySelector,
    useCoachingChatMessagesIdsSelector,
    //
    useCoachingChatInputValueSelector,
    useCoachingChatInputStateSelector
}
